/**
 * La tarjeta que se comparte: una imagen 1200x630 dibujada en canvas, sin dependencias.
 *
 * QUÉ NO LLEVA, y es deliberado: ni el importe invertido, ni la dirección del vault, ni la wallet.
 * Compartir un resultado no puede obligar a enseñar cuánto dinero tienes ni dónde está — el
 * porcentaje y el tiempo cuentan lo mismo y no dejan a nadie expuesto. Quien quiera enseñar la cifra
 * puede decirla él.
 *
 * 1200x630 es la medida que esperan Twitter/X, Telegram, Discord y LinkedIn (relación 1,91:1). Con
 * otra proporción la recortan por donde les parece, normalmente por el centro.
 */

export type DatosTarjeta = {
  par: string;
  red: string;
  /** comisiones generadas sobre el capital aportado, en % */
  pct: number | null;
  /** el mismo ritmo extrapolado a un año, en % */
  anual: number | null;
  /** "1 día 21 h 26 min" */
  tiempo: string;
  enRango: boolean;
  /** el rango y el precio de ahora, ya en la orientación en la que se enseñan */
  banda: { lo: number; hi: number; px: number; unidad: string } | null;
};

const C = {
  fondo: "#07131a",
  panel: "#0e1e28",
  linea: "#1b3140",
  texto: "#eaf2f5",
  tenue: "#7e99a8",
  marea: "#46c39a",
  baliza: "#f2653a",
};

const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, monospace';
const DISPLAY = '"IBM Plex Sans Condensed", "IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';

const nf = (x: number, d = 2) =>
  x.toLocaleString("es", { minimumFractionDigits: d, maximumFractionDigits: d });

/**
 * @returns `png` para pintarla —un data URL, que NO se puede revocar ni caducar— y `blob` para
 *          copiarla o descargarla.
 *
 * Se devuelven las dos formas a propósito. Con solo un `blob:` de `createObjectURL`, cualquier
 * remonte o limpieza del componente que la enseña invalida la URL y el `<img>` se queda roto
 * enseñando el texto alternativo — que es exactamente lo que pasó el 2026-08-16. El data URL pesa
 * un tercio más en memoria y a cambio no depende del ciclo de vida de nadie.
 */
export async function dibujarTarjeta(d: DatosTarjeta): Promise<{ png: string; blob: Blob }> {
  // Sin esto, el canvas dibuja con la fuente de reserva: las tipografías de la web se cargan
  // asincronamente y el primer `fillText` puede llegar antes que ellas.
  try { await document.fonts.ready; } catch { /* navegador sin la API: se dibuja igual */ }

  const W = 1200, H = 630;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const g = cv.getContext("2d");
  if (!g) throw new Error("este navegador no sabe dibujar la tarjeta");

  // ---- fondo, con un halo hacia el acento para que no sea un rectángulo plano
  g.fillStyle = C.fondo;
  g.fillRect(0, 0, W, H);
  const halo = g.createRadialGradient(W * 0.78, H * 0.12, 40, W * 0.78, H * 0.12, 620);
  halo.addColorStop(0, "rgba(70,195,154,0.14)");
  halo.addColorStop(1, "rgba(7,19,26,0)");
  g.fillStyle = halo;
  g.fillRect(0, 0, W, H);

  // ---- marca
  g.fillStyle = C.texto;
  g.font = `600 30px ${DISPLAY}`;
  g.fillText("Keellabs", 64, 84);
  g.fillStyle = C.tenue;
  g.font = `500 17px ${MONO}`;
  g.fillText("Liquidez concentrada · Uniswap V3", 64, 114);

  // ---- estado, arriba a la derecha
  const pill = d.enRango ? "COBRANDO" : "FUERA DE RANGO";
  const col = d.enRango ? C.marea : C.baliza;
  g.font = `600 16px ${MONO}`;
  const anchoPill = g.measureText(pill).width + 34;
  g.fillStyle = `${col}22`;
  redondeado(g, W - 64 - anchoPill, 58, anchoPill, 38, 19);
  g.fill();
  g.strokeStyle = `${col}66`;
  g.lineWidth = 1;
  redondeado(g, W - 64 - anchoPill, 58, anchoPill, 38, 19);
  g.stroke();
  g.fillStyle = col;
  g.fillText(pill, W - 64 - anchoPill + 17, 83);

  // ---- el par
  g.fillStyle = C.texto;
  g.font = `600 66px ${DISPLAY}`;
  g.fillText(d.par, 64, 214);
  g.fillStyle = C.tenue;
  g.font = `500 20px ${MONO}`;
  g.fillText(d.red, 64, 248);

  // ---- LA CIFRA
  if (d.pct != null) {
    g.fillStyle = C.marea;
    g.font = `600 128px ${MONO}`;
    const txt = `${d.pct < 0.01 ? "<0,01" : nf(d.pct, d.pct < 1 ? 3 : 2)}%`;
    g.fillText(txt, 64, 388);

    g.fillStyle = C.tenue;
    g.font = `500 24px ${MONO}`;
    g.fillText("en comisiones sobre mi capital", 64, 428);

    if (d.anual != null) {
      g.fillStyle = C.texto;
      g.font = `500 26px ${MONO}`;
      g.fillText(`≈ ${nf(d.anual, d.anual < 10 ? 1 : 0)}% anual a este ritmo`, 64, 470);
    }
  }

  // ---- el tiempo, a la derecha de la cifra
  g.fillStyle = C.tenue;
  g.font = `500 20px ${MONO}`;
  g.textAlign = "right";
  g.fillText("FUNCIONANDO", W - 64, 350);
  g.fillStyle = C.texto;
  g.font = `600 40px ${MONO}`;
  g.fillText(d.tiempo, W - 64, 398);
  g.textAlign = "left";

  // ---- la banda: el dibujo que explica el producto sin leer
  if (d.banda) {
    const bx = 64, bw = W - 128, by = 512, bh = 30;
    g.fillStyle = C.panel;
    redondeado(g, bx, by, bw, bh, 8);
    g.fill();
    g.strokeStyle = C.linea;
    redondeado(g, bx, by, bw, bh, 8);
    g.stroke();

    const { lo, hi, px } = d.banda;
    const min = Math.min(lo, px) * 0.985;
    const max = Math.max(hi, px) * 1.015;
    const aX = (v: number) => bx + ((v - min) / (max - min)) * bw;

    g.fillStyle = "rgba(70,195,154,0.20)";
    g.fillRect(aX(lo), by, Math.max(2, aX(hi) - aX(lo)), bh);

    g.fillStyle = d.enRango ? C.marea : C.baliza;
    g.fillRect(aX(px) - 2, by - 7, 4, bh + 14);

    g.fillStyle = C.tenue;
    g.font = `500 15px ${MONO}`;
    g.fillText(`${nf(lo, lo < 10 ? 4 : 2)}`, bx, by - 12);
    g.textAlign = "right";
    g.fillText(`${nf(hi, hi < 10 ? 4 : 2)}  ${d.banda.unidad}`, bx + bw, by - 12);
    g.textAlign = "left";
  }

  // ---- pie
  g.fillStyle = C.baliza;
  g.font = `600 22px ${MONO}`;
  g.fillText("beta.keellabs.app", 64, H - 34);
  g.fillStyle = C.tenue;
  g.font = `500 18px ${MONO}`;
  g.fillText("· tu vault, tus llaves", 64 + g.measureText("beta.keellabs.app").width + 44, H - 34);

  const blob = await new Promise<Blob>((ok, err) =>
    cv.toBlob((b) => (b ? ok(b) : err(new Error("no pude generar la imagen"))), "image/png")
  );
  return { png: cv.toDataURL("image/png"), blob };
}

function redondeado(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** El texto que acompaña a la tarjeta. Sin el importe, por lo mismo que la imagen. */
export function textoCompartir(d: DatosTarjeta): string {
  const cifra = d.pct != null
    ? `${d.pct < 0.01 ? "<0,01" : nf(d.pct, d.pct < 1 ? 3 : 2)}% en comisiones`
    : "generando comisiones";
  const ritmo = d.anual != null ? ` (≈${nf(d.anual, 0)}% anual a este ritmo)` : "";
  return `Mi piscina ${d.par} lleva ${d.tiempo} rebalanceándose sola y ha generado ${cifra}${ritmo}.\n\nLiquidez concentrada en Uniswap V3, sin custodia: el vault es mío y el robot solo puede mover el rango.\n\n`;
}
